import { useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';

import { ESSENTIAL_COOKIES } from 'app';
import {
  appConfigSelector,
  isAuthenticatedSelector,
  STATUS_NEED_TO_FILL_FIRST_LOGIN_FORM,
  userStatusSelector,
} from 'reducers/app.reducer';
import { isHomepage as isHomepagePath } from './is-homepage';

/**
 * Hook to handle redirection to an external homepage.
 * Returns `isRedirecting: true` if a redirect is in progress or pending,
 * allowing components to display a loading state.
 */
export const useExternalRedirect = (): {
  isRedirecting: boolean;
  isCheckingRedirect: boolean;
} => {
  // Use window.location.pathname to get the real path (not relative to the Router basename)
  const currentPathname = window.location.pathname;
  const config = useSelector(appConfigSelector);
  const { externalHomePageUrl, appLocale } = config;
  const [cookie] = useCookies([ESSENTIAL_COOKIES]);
  const userStatus = useSelector(userStatusSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  // Tant qu'on est authentifié mais que le statut user n'est pas chargé,
  // on ne peut pas savoir si l'utilisateur doit remplir le first-login form.
  // On doit donc attendre avant de décider du redirect.
  const isUserStatusLoading = isAuthenticated && userStatus == null;
  const needsToFillFirstLoginForm =
    userStatus === STATUS_NEED_TO_FILL_FIRST_LOGIN_FORM;

  const isHomepage = useMemo(
    () => isHomepagePath(currentPathname),
    [currentPathname],
  );

  // Is the config already loaded?
  const isConfigLoaded = Object.keys(config).length > 0;

  // Check if we're waiting for the config (or user status) to decide on redirect
  const isCheckingRedirect =
    isHomepage &&
    isAuthenticated &&
    !needsToFillFirstLoginForm &&
    (!isConfigLoaded || isUserStatusLoading);

  const shouldRedirect = Boolean(
    isHomepage &&
      externalHomePageUrl &&
      isAuthenticated &&
      !needsToFillFirstLoginForm &&
      !isUserStatusLoading,
  );

  useEffect(() => {
    if (shouldRedirect && externalHomePageUrl) {
      window.location.href = `${externalHomePageUrl}?redirect_to=/${appLocale !== 'en' ? appLocale : ''}`;
    }
  }, [shouldRedirect, externalHomePageUrl, appLocale]);

  return {
    isRedirecting: shouldRedirect || isCheckingRedirect,
    isCheckingRedirect,
  };
};

export default useExternalRedirect;
